import * as React from 'react';
import {useMemo} from 'react';
import {useTheme} from '@mui/material';
import type {RichTreeViewProProps} from '@mui/x-tree-view-pro';
import {RichTreeViewPro} from '@mui/x-tree-view-pro';
import type {ThesaurusValue} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {ChevronLeftIcon} from 'components/icons/ChevronLeftIcon';
import {ChevronRightIcon} from 'components/icons/ChevronRightIcon';
import type {ThesaurusTreeViewItem, TreeNode} from '../types';
import {
  ThesaurusSelectionContext,
  useThesaurusSelectionContextProviderValue,
} from '../contexts/ThesaurusSelectionContext';
import {ThesaurusItem} from './ThesaurusItem';

export const THESAURUS_TREE_ITEM_HEIGHT = 50;

// Use RichTreeViewProProps<Item, Multiple> to be able to use controlled
// tree view with empty initial selected although only one node may be
// selected at a time.
type Props = Omit<
  RichTreeViewProProps<ThesaurusTreeViewItem, true>,
  'items'
> & {
  // The data structure
  data: TreeNode[];
  /**
   * Terms (node ids) set.
   * Nodes will be marked in the tree.
   */
  thesaurusValue?: ThesaurusValue;
  /**
   * gets the display code for a node, if any
   */
  getDisplayCode: (node: TreeNode) => string | undefined;
  /**
   * Current focused node id, if any,
   * either node clicked by user in the tree or
   * term clicked in the work.
   */
  focusedNodeId?: string;
  onlyTopLevel?: boolean;
};

export const Thesaurus: React.FC<Props> = ({
  data,
  thesaurusValue,
  getDisplayCode,
  focusedNodeId,
  onlyTopLevel,
  ...props
}) => {
  const {t} = useLocalization();
  const theme = useTheme();
  const contextValue = useThesaurusSelectionContextProviderValue(
    data,
    thesaurusValue,
    focusedNodeId,
  );

  const items = useMemo(() => {
    const mapNode = (node: TreeNode): ThesaurusTreeViewItem => ({
      id: node.id,
      label: node.deactivated
        ? `${node.label} (${t('general.deactivated')})`
        : node.label,
      displayCode: getDisplayCode?.(node),
      onlyTopLevel,
      children: onlyTopLevel ? undefined : node.children?.map(mapNode),
    });
    return data.map(mapNode);
  }, [data, getDisplayCode, onlyTopLevel, t]);

  return (
    <ThesaurusSelectionContext.Provider value={contextValue}>
      <RichTreeViewPro
        aria-label="thema thesaurus"
        itemHeight={THESAURUS_TREE_ITEM_HEIGHT}
        itemChildrenIndentation={theme.spacing(4)}
        {...props}
        items={items}
        defaultSelectedItems={undefined}
        selectedItems={focusedNodeId ? [focusedNodeId] : []}
        slots={{
          item: ThesaurusItem,
          collapseIcon: ChevronLeftIcon,
          expandIcon: ChevronRightIcon,
          ...props.slots,
        }}
        slotProps={{
          ...props.slotProps,
        }}
      />
    </ThesaurusSelectionContext.Provider>
  );
};
